import {
  isOptionAvailableForAllGenders,
  isOptionAvailableForGender,
} from '@common/helpers/genderRestriction';
import * as Sentry from '@sentry/nextjs';
import { useTranslations } from '@lang';
import rewriteHttp from '@utils/rewriteHttp';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { PackageExperiencesTemplate } from 'voyager';
import type {
  PackageExperiencesGuestMode,
  PackageExperiencesSection,
  PackageInclusion,
  PackageInclusionChoiceOption,
  PreferenceCategory,
} from 'voyager';

import { useCurrentStep, usePackageConfiguration, useWizardNavigation } from '../../contexts';
import { useNextStepLabel } from '../hooks/useNextStepLabel';
import { buildExperienceTimeslotOptions } from '../../helpers/buildExperienceTimeslotOptions';
import { findOptionFromConfigGroups } from '../../helpers/findOptionFromConfigGroups';
import { getEffectiveExperienceDate } from '../../helpers/getEffectiveExperienceDate';
import { heldStartTimesFor } from '../../helpers/heldSpaTimes';
import { useMultiGuestTimeslotAvailability } from '../../hooks/useMultiGuestTimeslotAvailability';
import { AvailabilityForm as AvailabilityFormType, PackagesFormValues } from '../../types';
import { useCheckSpaDayIsAvailable } from '../ExperiencesStep/RepeatingExperiencesStep/hooks/useCheckSpaDayIsAvailable';
import { useGuestExperiences } from '../hooks/useGuestExperiences';
import { ExperienceSlot, experienceGroupKey } from './buildExperienceSlots';
import { configGroupsCacheKey, useExperienceConfigGroups } from './useExperienceConfigGroups';
import { useExperienceSlots } from './useExperienceSlots';

const NOTES_CATEGORY = 'notes';

export const ExperiencesStepV2 = () => {
  const { t } = useTranslations();
  const nextLabel = useNextStepLabel('common.next');
  const router = useRouter();
  const {
    property,
    language,
    journeyParams,
    rate,
    room,
    spaPackageId,
    stayDuration,
    spaGuestAmount,
    spaOpenTime,
    spaCloseTime,
    reservedSpaTimeSlots,
    heldSpaTimes,
    spaTimeBands,
  } = usePackageConfiguration();
  const { allFormSteps, currentFormStep, handleNextStep, handlePreviousStep, currentStepIndex } =
    useWizardNavigation();
  const { packageTotalPrice, isSubmitting, itinerary } = useCurrentStep();
  const { watch, setValue, getValues } = useFormContext<PackagesFormValues>();

  const numberOfGuests = spaGuestAmount;
  const selectedExperiences = watch('selectedExperiences');
  const guests = watch('guests') || [];
  const spaNotes = watch('spaNotes') ?? '';
  const allGuestExperiences = useGuestExperiences();

  const [showErrors, setShowErrors] = useState(false);
  // Bumped per refused submit, so a second attempt moves focus again.
  const [focusErrorToken, setFocusErrorToken] = useState(0);
  const hasReportedUnavailable = useRef(false);
  const separateSelections = useRef<
    (PackagesFormValues['guests'][number]['experiences'] | undefined)[] | null
  >(null);
  const [notesExpanded, setNotesExpanded] = useState(!!spaNotes);

  const { spaDay, packageConfigGroups, slots } = useExperienceSlots(currentFormStep);

  // A group's guests share a slot only while every entry agrees. A package of
  // only choice groups has no entries until the guest picks one, so an explicit
  // choice is held here and fanned out to entries as they are created.
  const [chosenGuestMode, setChosenGuestMode] = useState<PackageExperiencesGuestMode | null>(null);

  const isGroupMode =
    numberOfGuests > 1 &&
    (chosenGuestMode
      ? chosenGuestMode === 'group'
      : !(selectedExperiences || []).some((exp) => exp.guestsUseSameTime === false));
  const guestMode: PackageExperiencesGuestMode = isGroupMode ? 'group' : 'separate';

  const quantity = isGroupMode ? numberOfGuests : 1;
  const guestIndexes = isGroupMode ? [0] : Array.from({ length: numberOfGuests }, (_, i) => i);

  const bookingAt = (guestIndex: number, slotIndex: number) =>
    guests[guestIndex]?.experiences?.[slotIndex];

  const guestGenders = guests.map((guest) => guest?.guestDetails?.gender);
  const guestGendersKey = guestGenders.join('|');

  const optionGender = (optionId: number) =>
    findOptionFromConfigGroups(packageConfigGroups, optionId)?.genderSpecific;

  const isOfferedToEveryGuest = (optionId: number) =>
    isOptionAvailableForAllGenders(optionGender(optionId), guestGenders);

  // Group mode copies guest 1's choice to every guest, so it offers only what the whole party can take.
  const isOptionOfferedTo = (guestIndex: number, optionId: number) =>
    isGroupMode
      ? isOfferedToEveryGuest(optionId)
      : isOptionAvailableForGender(optionGender(optionId), guestGenders[guestIndex]);

  const stayDates = useMemo(
    () =>
      Array.from({ length: stayDuration + 1 }, (_, index) =>
        moment(journeyParams.fromDate).add(index, 'days').format('YYYY-MM-DD')
      ),
    [journeyParams.fromDate, stayDuration]
  );

  const hasDayChoice = slots.some((slot) => slot.dayNumberRange.length > 1);

  const { availableDates } = useCheckSpaDayIsAvailable({
    propertyId: Number(property.id),
    spaPackageId: Number(spaPackageId),
    datesToCheck: stayDates,
    numberOfGuests: quantity,
    enabled: hasDayChoice,
  });

  const daysTakenBySibling = (slot: ExperienceSlot, guestIndex: number) =>
    new Set(
      slots
        .filter((other) => other.index !== slot.index && other.groupKey !== slot.groupKey)
        .filter((other) => String(other.optionGroupId) === String(slot.optionGroupId))
        .map((other) => bookingAt(guestIndex, other.index)?.dayNumber)
        .filter((dayNumber): dayNumber is number => dayNumber !== undefined)
    );

  const isDayAvailable = (dayNumber: number) =>
    !availableDates.length ||
    availableDates.includes(
      moment(journeyParams.fromDate).add(dayNumber, 'days').format('YYYY-MM-DD')
    );

  /**
   * A card defaults to a day the guest could actually pick, so the field, the
   * times listed and the availability fetched all describe the same day.
   */
  const defaultDayFor = (slot: ExperienceSlot, guestIndex: number) => {
    const taken = daysTakenBySibling(slot, guestIndex);

    return (
      slot.dayNumberRange.find((dayNumber) => isDayAvailable(dayNumber) && !taken.has(dayNumber)) ??
      slot.dayNumberRange[0]
    );
  };

  const dateForSlot = (slot: ExperienceSlot, guestIndex: number) =>
    getEffectiveExperienceDate(
      journeyParams.fromDate,
      bookingAt(guestIndex, slot.index)?.dayNumber ?? defaultDayFor(slot, guestIndex),
      slot.dayNumberRange
    );

  const { canGuestsShareTimeslotOn, hasTimeslotsOn } = useMultiGuestTimeslotAvailability({
    dates: slots.map((slot) => dateForSlot(slot, 0).formattedDate),
    numberOfGuests,
    propertyId: Number(property.id),
    spaPackageId: Number(spaPackageId),
    spaOpenTime,
    spaCloseTime,
  });

  const optionIdsForSlot = (slot: ExperienceSlot) =>
    slot.kind === 'included'
      ? [Number(slot.option?.optionId)]
      : (slot.choiceGroup?.options || []).map((option) => Number(option.optionId));

  const groupOptionIdsForSlot = (slot: ExperienceSlot) =>
    slot.kind === 'choice'
      ? optionIdsForSlot(slot).filter(isOfferedToEveryGuest)
      : optionIdsForSlot(slot);

  const hasNoSharedTime =
    numberOfGuests > 1 &&
    slots.some((slot) => {
      const { formattedDate } = dateForSlot(slot, 0);
      const optionIds = groupOptionIdsForSlot(slot);

      return (
        hasTimeslotsOn(formattedDate, optionIds) &&
        canGuestsShareTimeslotOn(formattedDate, optionIds) === false
      );
    });

  const hasTooFewOptionsForEveryGender =
    numberOfGuests > 1 &&
    slots.some((slot) => {
      if (slot.kind !== 'choice') return false;
      const optionIds = optionIdsForSlot(slot);
      const offered = optionIds.filter(isOfferedToEveryGuest).length;

      return offered < optionIds.length && offered < (slot.choiceGroup?.selectionsRequired ?? 1);
    });

  const isGroupModeUnavailable = hasNoSharedTime || hasTooFewOptionsForEveryGender;

  const configGroupRequests = slots.flatMap((slot) =>
    guestIndexes.map((guestIndex) => ({
      date: dateForSlot(slot, guestIndex).formattedDate,
      quantity,
    }))
  );

  const { byKey, loadingKeys, hasError } = useExperienceConfigGroups({
    requests: configGroupRequests,
    configurationGroups: packageConfigGroups,
    propertyId: Number(property.id),
    spaPackageId: Number(spaPackageId),
    spaOpenTime,
    spaCloseTime,
  });

  const dayLabel = (dayNumber: number) =>
    moment(journeyParams.fromDate).add(dayNumber, 'days').locale(language).format('dddd, D MMMM');

  const dayOptionsFor = (slot: ExperienceSlot, guestIndex: number) => {
    const takenBySibling = daysTakenBySibling(slot, guestIndex);

    return slot.dayNumberRange.filter(isDayAvailable).map((dayNumber) => ({
      value: String(dayNumber),
      label: dayLabel(dayNumber),
      disabled: takenBySibling.has(dayNumber),
    }));
  };

  const timeOptionsFor = (slot: ExperienceSlot, guestIndex: number, optionId: number) => {
    const { formattedDate, effectiveDayNumber } = dateForSlot(slot, guestIndex);
    const configGroups = byKey[configGroupsCacheKey(formattedDate, quantity)] || [];
    const configOption = findOptionFromConfigGroups(configGroups, optionId);

    const experienceId =
      slot.kind === 'included'
        ? slot.option?.id
        : slot.choiceGroup?.options.find((option) => Number(option.optionId) === optionId)?.id;

    const entityType =
      slot.kind === 'included'
        ? slot.option?.type
        : slot.choiceGroup?.options.find((option) => Number(option.optionId) === optionId)
            ?.experienceType;

    if (!experienceId || !entityType) return [];

    return buildExperienceTimeslotOptions({
      timeslots: configOption?.timeslots || [],
      experienceId: Number(experienceId),
      entityType,
      delayAfter: slot.delayAfter,
      optionId,
      optionGroupId: slot.optionGroupId,
      experienceDate: formattedDate,
      activeDayNumber: effectiveDayNumber,
      selectedExperiences,
      allGuestExperiences,
      guestIndex,
      reservedSpaTimeSlots,
      heldStartTimes: heldStartTimesFor(
        heldSpaTimes,
        (isGroupMode ? guests : [guests[guestIndex]]).flatMap((guest) =>
          guest?.guestDetails ? [guest.guestDetails] : []
        ),
        formattedDate
      ),
      minimumTimeslotQuantity: quantity,
      currentBookingExperienceId: bookingAt(guestIndex, slot.index)?.experienceId,
      currentBookingOptionId: bookingAt(guestIndex, slot.index)?.optionId,
      includeOwnClashes: true,
      timeBands: spaTimeBands?.[formattedDate],
    });
  };

  const timeDropdownOptions = (slot: ExperienceSlot, guestIndex: number, optionId: number) =>
    timeOptionsFor(slot, guestIndex, optionId).map((timeslot) => ({
      value: timeslot.startTime,
      label: timeslot.startTime.slice(0, 5),
      disabled: timeslot.isUnavailable,
    }));

  const writeSlot = (
    guestIndex: number,
    slotIndex: number,
    value: Partial<AvailabilityFormType> | null
  ) => {
    // Once group mode has been used, its choices are what the guest last saw,
    // so the rows put aside on the way in are no longer the ones to hand back.
    if (isGroupMode) separateSelections.current = null;

    setSlotValue(guestIndex, slotIndex, value);
  };

  /**
   * Writes without retiring the separate-mode rows, for changes the guest did
   * not make here, so switching back still hands their own choices back.
   */
  const setSlotValue = (
    guestIndex: number,
    slotIndex: number,
    value: Partial<AvailabilityFormType> | null
  ) => {
    const targets = isGroupMode
      ? (getValues('guests') || []).map((_, index) => index)
      : [guestIndex];

    targets.forEach((index) => {
      setValue(`guests.${index}.experiences.${slotIndex}`, value as AvailabilityFormType, {
        shouldValidate: false,
        shouldDirty: true,
      });
    });
  };

  /**
   * Rebuilds the chosen entries from what the guests actually hold, so `count`
   * stays the per-guest tally ecom1 keeps and no stale entry survives.
   */
  const syncChosenExperiences = () => {
    const currentGuests = getValues('guests') || [];
    const current = getValues('selectedExperiences') || [];
    const choiceKeys = new Set(slots.filter((s) => s.kind === 'choice').map((s) => s.groupKey));

    const kept = current.filter(
      (exp) =>
        exp.type === 'spa-extras' ||
        exp.isIncluded ||
        !choiceKeys.has(experienceGroupKey(Number(exp.dayNumber ?? 0), String(exp.optionGroupId)))
    );

    const tally = new Map<string, { entry: (typeof current)[number]; count: number }>();

    slots
      .filter((slot) => slot.kind === 'choice')
      .forEach((slot) => {
        currentGuests.forEach((_, guestIndex) => {
          const booking = currentGuests[guestIndex]?.experiences?.[slot.index];
          if (!booking?.optionId) return;

          const option = slot.choiceGroup?.options.find(
            (entry) => Number(entry.optionId) === Number(booking.optionId)
          );
          if (!option) return;

          const key = `${slot.groupKey}:${booking.optionId}`;
          const existing = tally.get(key);
          if (existing) {
            existing.count += 1;
            return;
          }

          const previous = current.find(
            (exp) =>
              String(exp.optionGroupId) === String(slot.optionGroupId) &&
              Number(exp.optionId) === Number(booking.optionId) &&
              Number(exp.dayNumber ?? 0) === slot.dayNumber
          );

          tally.set(key, {
            count: 1,
            entry: {
              id: Number(option.id),
              optionId: Number(option.optionId),
              type: option.experienceType,
              optionGroupId: slot.optionGroupId,
              delayAfter: slot.delayAfter,
              dayNumber: slot.dayNumber,
              dayNumberRange: slot.dayNumberRange,
              isIncluded: false,
              numberOfGroupSelectionsRequired: slot.choiceGroup?.selectionsRequired || 1,
              guestsUseSameTime: previous?.guestsUseSameTime ?? isGroupMode,
              count: 0,
            } as (typeof current)[number],
          });
        });
      });

    setValue(
      'selectedExperiences',
      [...kept, ...[...tally.values()].map(({ entry, count }) => ({ ...entry, count }))],
      { shouldDirty: true }
    );
  };

  const slotsForGroup = (groupKey: string) =>
    slots.filter((slot) => slot.kind === 'choice' && slot.groupKey === groupKey);

  const handleChoiceToggle = (guestIndex: number, groupKey: string, optionId: string) => {
    const groupSlots = slotsForGroup(groupKey);
    const group = groupSlots[0]?.choiceGroup;
    if (!group) return;

    const target = Number(optionId);
    const occupied = groupSlots.find(
      (slot) => Number(bookingAt(guestIndex, slot.index)?.optionId) === target
    );

    if (occupied) {
      writeSlot(guestIndex, occupied.index, null);
      syncChosenExperiences();
      return;
    }

    const free = groupSlots.find((slot) => !bookingAt(guestIndex, slot.index)?.optionId);
    // A one-option group swaps rather than refusing the click.
    const slot = free ?? (group.selectionsRequired === 1 ? groupSlots[0] : undefined);
    if (!slot) return;

    const option = group.options.find((entry) => Number(entry.optionId) === target);
    if (!option || !isOptionOfferedTo(guestIndex, target)) return;

    writeSlot(guestIndex, slot.index, {
      experienceId: Number(option.id),
      optionId: target,
      optionGroupId: slot.optionGroupId,
      type: option.experienceType,
      delayAfter: slot.delayAfter,
      dayNumber: bookingAt(guestIndex, slot.index)?.dayNumber ?? slot.dayNumberRange[0],
    });
    syncChosenExperiences();
  };

  /**
   * Rebuilt rather than spread, so no staff, room or date field survives from
   * the day being left behind.
   */
  const handleDayChange = (guestIndex: number, slotIndex: number, value: string) => {
    const current = bookingAt(guestIndex, slotIndex);
    const slot = slots[slotIndex];

    writeSlot(guestIndex, slotIndex, {
      experienceId: current?.experienceId,
      optionId: current?.optionId,
      optionGroupId: current?.optionGroupId ?? slot?.optionGroupId,
      type: current?.type,
      delayAfter: current?.delayAfter ?? slot?.delayAfter,
      dayNumber: Number(value),
    } as unknown as AvailabilityFormType);
  };

  const handleTimeChange = (
    guestIndex: number,
    slot: ExperienceSlot,
    optionId: number,
    value: string
  ) => {
    const selected = timeOptionsFor(slot, guestIndex, optionId).find(
      (option) => option.startTime === value && !option.isUnavailable
    );

    if (!selected) return;

    writeSlot(guestIndex, slot.index, {
      ...selected,
      optionId: Number(selected.optionId),
      optionGroupId: selected.optionGroupId ?? '',
    } as unknown as AvailabilityFormType);
  };

  const buildInclusions = (guestIndex: number): PackageInclusion[] =>
    slots
      .filter((slot) => slot.kind === 'included')
      .map((slot) => {
        const optionId = Number(slot.option?.optionId);
        const booking = bookingAt(guestIndex, slot.index);
        const { formattedDate, effectiveDayNumber } = dateForSlot(slot, guestIndex);
        const cacheKey = configGroupsCacheKey(formattedDate, quantity);
        const packageOption = findOptionFromConfigGroups(packageConfigGroups, optionId);

        return {
          id: String(slot.index),
          title: packageOption?.name ?? (spaDay ? t('common.unavailable') : ''),
          ...(slot.dayNumberRange.length > 1
            ? {
                dayOptions: dayOptionsFor(slot, guestIndex),
                selectedDay: String(booking?.dayNumber ?? effectiveDayNumber),
              }
            : { fixedDayLabel: dayLabel(slot.dayNumberRange[0]) }),
          timeOptions: timeDropdownOptions(slot, guestIndex, optionId),
          selectedTime: booking?.startTime,
          isLoading: loadingKeys[cacheKey],
          noteText: t('spa.booking.timesSubjectToChange'),
          timeError:
            showErrors && !booking?.startTime ? t('package.experiences.timeRequired') : undefined,
        } satisfies PackageInclusion;
      });

  const buildChoiceGroups = (guestIndex: number) => {
    const groupKeys = [...new Set(slots.filter((s) => s.kind === 'choice').map((s) => s.groupKey))];

    return groupKeys.flatMap((groupKey) => {
      const groupSlots = slotsForGroup(groupKey);
      const group = groupSlots[0]?.choiceGroup;
      if (!group) return [];

      const maximumSelections = group.selectionsRequired;

      const slotForOption = (optionId: number) =>
        groupSlots.find((slot) => Number(bookingAt(guestIndex, slot.index)?.optionId) === optionId);

      const selectedOptionIds = groupSlots
        .map((slot) => bookingAt(guestIndex, slot.index)?.optionId)
        .filter((optionId): optionId is number => optionId !== undefined)
        .map(String);

      const offeredOptions = group.options.filter((option) =>
        isOptionOfferedTo(guestIndex, Number(option.optionId))
      );

      const options: PackageInclusionChoiceOption[] = offeredOptions.map((option) => {
        const optionId = Number(option.optionId);
        const slot = slotForOption(optionId);
        const isSelected = Boolean(slot);
        const activeSlot = slot ?? groupSlots[0];
        const booking = slot ? bookingAt(guestIndex, slot.index) : undefined;
        const { formattedDate, effectiveDayNumber } = dateForSlot(activeSlot, guestIndex);
        const configGroups = byKey[configGroupsCacheKey(formattedDate, quantity)] || [];
        const configOption = findOptionFromConfigGroups(configGroups, optionId);

        return {
          id: String(optionId),
          label: configOption?.name ?? option.name ?? '',
          imageUrl: configOption?.image?.url ? rewriteHttp(configOption.image.url) : undefined,
          ...(group.dayNumberRange.length > 1
            ? {
                dayOptions: dayOptionsFor(activeSlot, guestIndex),
                selectedDay: String(booking?.dayNumber ?? effectiveDayNumber),
              }
            : { fixedDayLabel: dayLabel(group.dayNumberRange[0]) }),
          timeOptions: isSelected ? timeDropdownOptions(activeSlot, guestIndex, optionId) : [],
          selectedTime: booking?.startTime,
          isLoading: isSelected && loadingKeys[configGroupsCacheKey(formattedDate, quantity)],
          noteText: t('spa.booking.timesSubjectToChange'),
          timeError:
            showErrors && isSelected && !booking?.startTime
              ? t('package.experiences.timeRequired')
              : undefined,
        };
      });

      const choiceGroup = {
        id: groupKey,
        subtitle: t('package.experiences.choiceSubtitle', { count: maximumSelections }),
        selectionCountText: t('package.experiences.selectionCount', {
          selected: selectedOptionIds.length,
          total: maximumSelections,
        }),
        maximumSelections,
        selectedOptionIds,
        options,
        optionError:
          showErrors && selectedOptionIds.length < maximumSelections
            ? t('package.experiences.optionRequired')
            : undefined,
      };

      return [choiceGroup];
    });
  };

  const guestSection = (guestIndex: number) => ({
    guestNumber: guestIndex + 1,
    selectedGuestId: String(guestIndex),
    existingGuests: guests.map((guest, index) => ({
      id: String(index),
      name: `${guest.guestDetails?.firstName ?? ''} ${guest.guestDetails?.lastName ?? ''}`.trim(),
      title: guest.guestDetails?.title ?? '',
      firstName: guest.guestDetails?.firstName ?? '',
      lastName: guest.guestDetails?.lastName ?? '',
      gender: guest.guestDetails?.gender ?? '',
    })),
    values: {
      title: guests[guestIndex]?.guestDetails?.title ?? '',
      firstName: guests[guestIndex]?.guestDetails?.firstName ?? '',
      lastName: guests[guestIndex]?.guestDetails?.lastName ?? '',
      gender: guests[guestIndex]?.guestDetails?.gender ?? '',
    },
  });

  const sections: PackageExperiencesSection[] = isGroupMode
    ? [{ id: 'group', inclusions: buildInclusions(0), choiceGroups: buildChoiceGroups(0) }]
    : guestIndexes.map((guestIndex) => ({
        id: String(guestIndex),
        guest: guestSection(guestIndex),
        inclusions: buildInclusions(guestIndex),
        choiceGroups: buildChoiceGroups(guestIndex),
      }));

  const guestIndexFor = (sectionId: string) => (isGroupMode ? 0 : Number(sectionId));

  /**
   * Keeps the options and days the guest already chose. Moving to group mode
   * keeps a time only when every guest holds that option at that time on that
   * day, because separate mode let each of them take it only while it still had
   * room, so it has room for the whole party. Any other time goes.
   *
   * Group mode is driven by guest 1's row, so the others' own choices are put
   * aside rather than overwritten, and handed back on the way out.
   */
  const handleGuestModeChange = (mode: PackageExperiencesGuestMode) => {
    const shouldShare = mode === 'group';
    if (shouldShare === isGroupMode) return;

    setChosenGuestMode(mode);

    setValue(
      'selectedExperiences',
      (getValues('selectedExperiences') || []).map((exp) => ({
        ...exp,
        guestsUseSameTime: shouldShare,
      })),
      { shouldDirty: true }
    );

    const currentGuests = getValues('guests') || [];

    if (shouldShare) {
      separateSelections.current = currentGuests.map((guest) => guest?.experiences);

      const isHeldByEveryGuest = (booking: NonNullable<AvailabilityFormType>, slotIndex: number) =>
        !!booking.startTime &&
        currentGuests.every((guest) => {
          const other = guest?.experiences?.[slotIndex];
          return (
            Number(other?.optionId) === Number(booking.optionId) &&
            other?.dayNumber === booking.dayNumber &&
            other?.startTime === booking.startTime
          );
        });

      const shared = (currentGuests[0]?.experiences || []).map((booking, slotIndex) =>
        booking && !isHeldByEveryGuest(booking, slotIndex)
          ? { ...booking, startTime: undefined, endTime: undefined, visitDate: undefined }
          : booking
      );

      currentGuests.forEach((_, index) => {
        setValue(`guests.${index}.experiences`, shared as never, { shouldDirty: true });
      });
    } else if (separateSelections.current) {
      separateSelections.current.forEach((experiences, index) => {
        if (index === 0 || index >= currentGuests.length || !experiences) return;
        setValue(`guests.${index}.experiences`, experiences as never, { shouldDirty: true });
      });
      separateSelections.current = null;
    }

    syncChosenExperiences();
  };

  useEffect(() => {
    if (isGroupModeUnavailable) handleGuestModeChange('separate');
  }, [isGroupModeUnavailable]);

  /**
   * Gender is set on an earlier step, so a guest who went back and changed a
   * title can hold a choice they can no longer take. It is cleared silently,
   * as on spa days. While group mode is about to give way to separate mode,
   * the clear waits, so guest 1 keeps a choice that suits them.
   */
  useEffect(() => {
    if (!spaDay || (isGroupMode && isGroupModeUnavailable)) return;

    let hasCleared = false;
    slots
      .filter((slot) => slot.kind === 'choice')
      .forEach((slot) => {
        guestIndexes.forEach((guestIndex) => {
          const optionId = bookingAt(guestIndex, slot.index)?.optionId;
          if (optionId == null || isOptionOfferedTo(guestIndex, Number(optionId))) return;

          setSlotValue(guestIndex, slot.index, null);
          hasCleared = true;
        });
      });

    if (hasCleared) syncChosenExperiences();
  }, [guestGendersKey, isGroupMode, isGroupModeUnavailable, spaDay, slots]);

  const hasLoadedConfigGroups =
    slots.length > 0 &&
    Object.keys(byKey).length > 0 &&
    !hasError &&
    !Object.values(loadingKeys).some(Boolean);

  const allOptionsUnavailable =
    hasLoadedConfigGroups &&
    slots.every((slot) => {
      const configGroups =
        byKey[configGroupsCacheKey(dateForSlot(slot, 0).formattedDate, quantity)];
      return optionIdsForSlot(slot).every(
        (optionId) =>
          (findOptionFromConfigGroups(configGroups || [], optionId)?.timeslots || []).length === 0
      );
    });

  // Mirrors the ecom1 report: the package passed the availability check that
  // got the guest here, yet nothing can actually be booked.
  useEffect(() => {
    if (!allOptionsUnavailable) {
      hasReportedUnavailable.current = false;
      return;
    }

    if (hasReportedUnavailable.current) return;
    hasReportedUnavailable.current = true;

    Sentry.captureEvent({
      message:
        'All spa options are unavailable for overnight package despite surpassing inital availability check',
      tags: {
        propertyId: property.id,
        spaPackageId,
        fromDate: moment(journeyParams.fromDate).format('YYYY-MM-DD'),
        toDate: moment(journeyParams.toDate).format('YYYY-MM-DD'),
        numberOfGuests,
      },
    });
  }, [allOptionsUnavailable]);

  const isValid = sections.every(
    (section) =>
      section.inclusions.every((inclusion) => !!inclusion.selectedTime) &&
      section.choiceGroups.every(
        (group) =>
          group.selectedOptionIds.length >= (group.maximumSelections ?? 1) &&
          group.options
            .filter((option) => group.selectedOptionIds.includes(option.id))
            .every((option) => !!option.selectedTime)
      )
  );

  const handleNext = () => {
    if (!isValid) {
      setShowErrors(true);
      setFocusErrorToken((token) => token + 1);
      return;
    }
    handleNextStep();
  };

  const handleBack = () => (currentStepIndex > 0 ? handlePreviousStep() : router.back());

  // Runs after the render that marks the fields, so the first one exists to take focus.
  useEffect(() => {
    if (!focusErrorToken) return;

    const page = document.querySelector('[data-testid="package-experiences-content-panel"]');
    const firstError = (page ?? document).querySelector<HTMLElement>(
      '[data-error="true"], [aria-invalid="true"]'
    );

    firstError?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    firstError?.focus?.({ preventScroll: true });
  }, [focusErrorToken]);

  const preferences: PreferenceCategory[] = [
    {
      id: NOTES_CATEGORY,
      label: t('additional.notes'),
      isFreeText: true,
      collapsible: true,
      expanded: notesExpanded,
      freeTextPlaceholder: t('booking.additionnalNotesPlaceholder'),
      freeTextValue: spaNotes,
    },
  ];

  const selectedRoom = rate.rooms?.find(({ id }) => String(id) === String(room.id));
  const imageSource = rate.imageUrl || rate.images?.[0] || selectedRoom?.imageUrl || undefined;

  return (
    <PackageExperiencesTemplate
      imageUrl={imageSource ? rewriteHttp(imageSource) : undefined}
      imageAlt={rate.title}
      sections={sections}
      guestMode={guestMode}
      onGuestModeChange={handleGuestModeChange}
      showGuestModeChoice={numberOfGuests > 1}
      isGroupModeUnavailable={isGroupModeUnavailable}
      errorMessage={hasError ? t('error.reservation_failed') : undefined}
      isGuestSelectionReadOnly
      onExistingGuestSelect={() => {}}
      onGuestChange={() => {}}
      onDayChange={(sectionId, id, value) =>
        handleDayChange(guestIndexFor(sectionId), Number(id), value)
      }
      onTimeChange={(sectionId, id, value) => {
        const slot = slots[Number(id)];
        if (slot?.option) {
          handleTimeChange(guestIndexFor(sectionId), slot, Number(slot.option.optionId), value);
        }
      }}
      onChoiceToggle={(sectionId, groupId, optionId) =>
        handleChoiceToggle(guestIndexFor(sectionId), groupId, optionId)
      }
      onChoiceDayChange={(sectionId, groupId, optionId, value) => {
        const guestIndex = guestIndexFor(sectionId);
        const slot = slotsForGroup(groupId).find(
          (entry) => Number(bookingAt(guestIndex, entry.index)?.optionId) === Number(optionId)
        );
        if (slot) handleDayChange(guestIndex, slot.index, value);
      }}
      onChoiceTimeChange={(sectionId, groupId, optionId, value) => {
        const guestIndex = guestIndexFor(sectionId);
        const slot = slotsForGroup(groupId).find(
          (entry) => Number(bookingAt(guestIndex, entry.index)?.optionId) === Number(optionId)
        );
        if (slot) handleTimeChange(guestIndex, slot, Number(optionId), value);
      }}
      preferences={preferences}
      onPreferenceExpandedChange={(_, expanded) => setNotesExpanded(expanded)}
      onPreferenceOptionToggle={() => {}}
      onPreferenceOtherChange={() => {}}
      onPreferenceFreeTextChange={(_, value) => setValue('spaNotes', value)}
      onBack={handleBack}
      onItineraryClick={itinerary?.onOpen}
      currentStep={currentStepIndex + 1}
      totalSteps={allFormSteps.length}
      footerTitle={rate.title}
      totalAmount={packageTotalPrice.amount}
      currencyCode={packageTotalPrice.currencyCode}
      locale={language}
      onNext={handleNext}
      isLoading={isSubmitting}
      labels={{
        title: t('package.experiences.title'),
        titleAccent: t('package.experiences.subtitle'),
        backAriaLabel: t('common.back'),
        progressLabel: t('aria.bookingProgress'),
        progressValueText: t('aria.bookingStep', {
          current: currentStepIndex + 1,
          total: allFormSteps.length,
        }),
        itineraryLabel: itinerary?.label,
        guestModeTitle: t('package.experiences.guestsTitle'),
        guestModeSubtitle: t('package.experiences.guestsSubtitle'),
        guestModeGroupLabel: t('package.experiences.chooseAsGroup'),
        guestModeGroupDescription: t('package.experiences.chooseAsGroupDescription'),
        guestModeSeparateLabel: t('package.experiences.chooseSeparately'),
        guestModeSeparateDescription: t('package.experiences.chooseSeparatelyDescription'),
        guestModeDisabledNote: hasNoSharedTime
          ? t('package.experiences.groupUnavailable')
          : t('package.experiences.groupUnavailableGender'),
        guestSubtitle: t('package.experiences.guestSubtitle'),
        guestTitleFormatter: (guestNumber: number) =>
          t('room.guestDetails.guestTitle', { number: guestNumber }),
        inclusions: {
          title: t('package.experiences.inclusionsTitle'),
          subtitle: t('package.experiences.inclusionsSubtitle'),
          dayLabel: t('package.experiences.selectDay'),
          timeLabel: t('package.experiences.selectTime'),
          noTimeslots: t('package.experiences.noTimeslots'),
          noteDismissLabel: t('spa.booking.dismissNote'),
        },
        choice: {
          title: t('package.experiences.inclusionsTitle'),
          timeslotHeading: t('package.experiences.whenWouldYouLike'),
          dayLabel: t('package.experiences.selectDay'),
          timeLabel: t('package.experiences.selectTime'),
          noTimeslots: t('package.experiences.noTimeslots'),
          noteDismissLabel: t('spa.booking.dismissNote'),
        },
        totalLabel: t('basket.total'),
        nextLabel,
      }}
    />
  );
};
